import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    // Check if user is authenticated from localStorage
    const stored = localStorage.getItem('isAuthenticated');
    return stored === 'true';
  });

  useEffect(() => {
    // Sync with localStorage
    if (isAuthenticated) {
      localStorage.setItem('isAuthenticated', 'true');
    } else {
      localStorage.removeItem('isAuthenticated');
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // If the backend says the token is invalid/expired, force a logout to return user to sign-in.
    apiService.setAuthFailureHandler(() => {
      setIsAuthenticated(false);
      localStorage.removeItem('authToken');
      // Hard redirect avoids leaving the app in a partially-authenticated state.
      if (window.location.pathname !== '/signin') {
        window.location.assign('/signin');
      }
    });

    return () => {
      apiService.setAuthFailureHandler(null);
    };
  }, []);

  const login = () => {
    setIsAuthenticated(true);
  };

  const logout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('authToken');
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

